import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useAppStore } from '../store';
import { T } from '../i18n';

// Color options
const TOILE_COLORS = [
  { name: 'Blanc Sablé', hex: '#F5F5F0' },
  { name: 'Écru', hex: '#F5F0DC' },
  { name: 'Beige', hex: '#D4C4A8' },
  { name: 'Sable', hex: '#C9B896' },
  { name: 'Taupe', hex: '#9B8B78' },
  { name: 'Gris', hex: '#8B8B8B' },
  { name: 'Anthracite', hex: '#4A4A4A' },
  { name: 'Noir', hex: '#1A1A1A' },
  { name: 'Terracotta', hex: '#C45C3E' },
  { name: 'Bordeaux', hex: '#6B2C3E' }
];

const RAL_COLORS = [
  { code: '7016', name: 'Gris anthracite', hex: '#383E42' },
  { code: '9016', name: 'Blanc', hex: '#F1F0EA' },
  { code: '9005', name: 'Noir', hex: '#0E0E10' },
  { code: '7035', name: 'Gris clair', hex: '#D0D0CE' },
  { code: '8019', name: 'Brun gris', hex: '#3D3635' },
  { code: '1015', name: 'Ivoire', hex: '#E6D2B5' },
  { code: '3004', name: 'Rouge', hex: '#701F29' },
  { code: '5003', name: 'Bleu', hex: '#232C3F' },
  { code: '6005', name: 'Vert', hex: '#0F4336' }
];

const LINER_COLORS = [
  { name: 'Bleu clair', hex: '#87CEEB' },
  { name: 'Bleu', hex: '#4682B4' },
  { name: 'Vert caraïbes', hex: '#48D1CC' },
  { name: 'Sable', hex: '#C2B280' },
  { name: 'Gris anthracite', hex: '#4A4A4A' }
];

const STRUCT_COLORS = [
  { name: 'Anthracite', hex: '#4A4A4A' },
  { name: 'Blanc', hex: '#F5F5F0' },
  { name: 'Gris', hex: '#808080' }
];

export default function ParamsPanel({ objectType, onClose, onValidate }) {
  const { lang, configs, updateConfig } = useAppStore();
  const t = T[lang] || T.fr;
  const config = configs[objectType];
  
  const update = (key, value) => {
    updateConfig(objectType, { [key]: value });
  };
  
  const renderSlider = (key, label, min, max, unit = 'cm') => (
    <div className="flex items-center gap-3 mb-4">
      <span className="text-xs text-muted-foreground w-16">{label}</span>
      <input
        type="range"
        id={`slider-${objectType}-${key}`}
        name={key}
        min={min}
        max={max}
        value={config[key]}
        onChange={(e) => update(key, parseInt(e.target.value))}
        className="flex-1"
        data-testid={`slider-${key}`}
      />
      <span className="text-sm font-bold text-secondary w-16 text-right">
        {config[key]}{unit}
      </span>
    </div>
  );
  
  const renderColorSwatches = (colors, key, isRal = false) => (
    <div className="flex flex-wrap gap-3 mb-4">
      {colors.map((color) => {
        const value = isRal ? color.code : color.name;
        const isSelected = config[key] === value;
        return (
          <button
            key={value}
            onClick={() => update(key, value)}
            className={`relative group ${isSelected ? 'scale-110' : ''}`}
            style={{ transition: 'transform 0.15s' }}
            data-testid={`color-${value}`}
          >
            <div
              className={`w-10 h-10 rounded-lg border-2 ${
                isSelected ? 'border-white shadow-neon-gold' : 'border-white/20'
              }`}
              style={{ backgroundColor: color.hex }}
            />
            {isRal && (
              <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px] text-muted-foreground whitespace-nowrap">
                {color.code}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
  
  const renderToggle = (key, label) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <button
        onClick={() => update(key, !config[key])}
        className={`w-12 h-7 rounded-full transition-colors ${
          config[key] ? 'bg-secondary' : 'bg-white/10'
        }`}
        data-testid={`toggle-${key}`}
      >
        <div
          className={`w-5 h-5 rounded-full bg-white transition-transform ${
            config[key] ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
  
  const renderParams = () => {
    switch (objectType) {
      case 'store':
        return (
          <>
            {renderSlider('width', t.width, 200, 600)}
            {renderSlider('height', t.height, 200, 500)}
            {renderSlider('depth', t.depth, 150, 400)}
            <p className="text-xs text-secondary font-bold mb-2 mt-4">{t.toile}</p>
            {renderColorSwatches(TOILE_COLORS, 'toile_color')}
            <p className="text-xs text-secondary font-bold mb-2 mt-6">{t.structure} (RAL)</p>
            {renderColorSwatches(RAL_COLORS, 'struct_color', true)}
            {renderToggle('led', 'LED')}
          </>
        );
        
      case 'pergola':
        return (
          <>
            {renderSlider('width', t.width, 300, 600)}
            {renderSlider('height', t.height, 200, 350)}
            {renderSlider('depth', t.depth, 250, 500)}
            <p className="text-xs text-secondary font-bold mb-2 mt-4">{t.structure}</p>
            {renderColorSwatches(STRUCT_COLORS, 'struct_color')}
            <div className="flex items-center gap-2 mt-4 mb-2">
              <span className="text-sm text-muted-foreground">{t.feet}:</span>
              <button
                onClick={() => update('feet', 2)}
                className={`px-4 py-2 rounded-lg border-2 ${
                  config.feet === 2 ? 'border-secondary text-secondary' : 'border-white/10 text-muted-foreground'
                }`}
                data-testid="feet-2"
              >
                2
              </button>
              <button
                onClick={() => update('feet', 4)}
                className={`px-4 py-2 rounded-lg border-2 ${
                  config.feet === 4 ? 'border-secondary text-secondary' : 'border-white/10 text-muted-foreground'
                }`}
                data-testid="feet-4"
              >
                4
              </button>
            </div>
          </>
        );
        
      
      default:
        return null;
    }
  };
  
  return (
    <>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed inset-x-0 bottom-0 glass-light rounded-t-3xl z-50 max-h-[75vh] overflow-hidden flex flex-col"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-white/10">
          <h3 className="font-display font-bold text-lg uppercase tracking-wide text-secondary">
            {t[objectType]}
          </h3>
          <button onClick={onClose} className="p-1">
            <X size={20} className="text-muted-foreground" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderParams()}
        </div>
        
        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium"
            data-testid="cancel-params"
          >
            {t.cancel}
          </button>
          <button
            onClick={onValidate}
            className="flex-1 btn-primary"
            data-testid="validate-params"
          >
            {t.validate_simulate}
          </button>
        </div>
      </motion.div>
    </>
  );
}
